import { api } from "./client";

export interface AsaInfo {
  assetId: string;
  assetName: string;
  unitName: string;
  manager: string;
  totalSupply: string;
  decimals: number;
  txId: string;
}

export interface TreasuryInfo {
  companyId: string;
  treasuryAddress: string;
  treasuryType: string;
  chainId: number;
  balance: {
    balanceWei: string;
    balanceEth: string;
  };
  cached: boolean;
  updatedAt: string;
}

export interface TokenInfo {
  id: string;
  companyId: string;
  name: string;
  symbol: string;
  decimals: number;
  totalSupply: string;
  contractAddress: string;
  chainId: number;
  createdAt: string;
}

export interface TokenBalance {
  tokenId: string;
  userId: string;
  balance: string;
  updatedAt: string;
}

export interface WalletTransaction {
  id: string;
  txHash: string;
  type: string;
  toAddress: string;
  amount: string;
  status: string;
  blockNumber: number | null;
  blockTimestamp: Date | null;
  createdAt: Date;
}

export interface LedgerTransactionsResult {
  address: string;
  transactions: WalletTransaction[];
  total: number;
  limit: number;
  offset: number;
}

export interface NetworkInfo {
  chainId: number;
  name: string;
  rpc: string;
}

export interface TokenBalancesResult {
  tokens: Array<TokenInfo & {
    company?: { id: string; name: string };
    balances?: {
      treasury: string;
      superboss: string;
      agentPool: string;
      totalSupply: string;
    };
    error?: string;
  }>;
  stats: {
    totalTokens: number;
    totalSupply: number;
    totalTreasury: number;
  };
}

export const blockchainLedgerApi = {
  // Get on-chain transactions for a company
  getTransactions: (companyId: string, limit?: number, offset?: number) =>
    api.get<LedgerTransactionsResult>(
      `/companies/${companyId}/ledger/transactions${
        limit ? `?limit=${limit}` : ""
      }${offset ? `&offset=${offset}` : ""}`
    ),

  // Get company treasury info
  getTreasury: (companyId: string) =>
    api.get<TreasuryInfo>(`/companies/${companyId}/ledger/treasury`),

  // Get company token balances
  getTokenBalances: (companyId: string) =>
    api.get<TokenBalancesResult>(`/companies/${companyId}/ledger/tokens`),

  // Manually sync a cost event to blockchain
  syncCostEvent: (companyId: string, costEventId: string) =>
    api.post<{ success: boolean; txHash?: string }>(
      `/companies/${companyId}/ledger/sync-cost/${costEventId}`,
      {}
    ),

  // Manually sync a finance event to blockchain
  syncFinanceEvent: (companyId: string, financeEventId: string) =>
    api.post<{ success: boolean; txHash?: string }>(
      `/companies/${companyId}/ledger/sync-finance/${financeEventId}`,
      {}
    ),

  // ASA onboarding (Algorand)
  createToken: (companyId: string) =>
    api.post<{
      success: boolean;
      asa?: AsaInfo;
      algorandAddress?: string;
      totalSupply?: string;
      escrowAppId?: string;
      registryAppId?: string;
      error?: string;
    }>(`/companies/${companyId}/ledger/token/create`, {}),

  getTokenStatus: (companyId: string) =>
    api.get<{
      hasToken: boolean;
      asa?: AsaInfo;
      algorandAddress?: string;
      escrowAppId?: string;
      registryAppId?: string;
      ammAppId?: string;
      asaManager?: string;
      asaClaimed?: boolean;
    }>(`/companies/${companyId}/ledger/token/status`),

  // Mint additional ASA supply (manager = superboss)
  mintAsa: (companyId: string, amount: number) =>
    api.post<{ success: boolean; txHash?: string; error?: string }>(
      `/companies/${companyId}/ledger/asa/mint`,
      { amount }
    ),

  // Get full Algorand onboarding status (ASA + escrow + registry)
  getOnboardStatus: (companyId: string) =>
    api.get<{
      companyId: string;
      hasAsa: boolean;
      asaAssetId?: string;
      asaManager?: string;
      asaClaimed: boolean;
      hasEscrow: boolean;
      escrowAppId?: string;
      escrowBalance?: number;
      hasRegistry: boolean;
      registryAppId?: string;
      agentCount?: number;
      ammAppId?: string;
      marketplaceAppID?: string;
    }>(`/companies/${companyId}/ledger/onboard/status`),

  // Escrow claim (company claims 90% share)
  prepareEscrowClaim: (
    companyId: string,
    escrowAppId: string,
    asaAssetId: string,
    amount: number,
    senderAddr: string
  ) =>
    api.post<{ success: boolean; unsignedTx?: string; error?: string }>(
      `/companies/${companyId}/ledger/escrow/claim/prepare`,
      { escrowAppId, asaAssetId, amount, senderAddr }
    ),

  submitEscrowClaim: (companyId: string, signedTx: string) =>
    api.post<{ success: boolean; txId?: string; error?: string }>(
      `/companies/${companyId}/ledger/escrow/claim/submit`,
      { signedTx }
    ),

  // Get blockchain network info
  getNetwork: () =>
    api.get<NetworkInfo>("/blockchain/network"),
};
